/**
 * The pay cycle: which dates each earnings period covers, and how that survives a
 * super admin changing the rule (spec 037). Pure — no Firestore import, no clock of
 * its own; every function that needs "now" takes it as a `Date`.
 *
 * ⚠️  THIS FILE IS A COPY. The original is `src/utils/payCycle.js` in the 121islamapp
 * repo, and the two MUST stay byte-identical below this header: the teacher's Pay
 * Slip and this dashboard have to agree on where every period starts, or the app
 * tells a teacher one thing and the sheet they are paid from says another — which is
 * the exact disagreement spec 037 was opened to end. `payCycle.test.js` beside this
 * file is the app's suite, copied with it; run both after any edit. If this ever
 * needs to change again, extract it to a shared package instead of editing twice.
 *
 * THE RULE IS VERSIONED IN TIME. `config/app.payCycle.versions` is an append-only
 * list, oldest first, each entry `{ startDay, effectiveFrom }`. A change never
 * rewrites the past: periods before it keep the rule that was in force when they
 * ran. That is the whole answer to counting a lesson twice — the boundaries this
 * module emits are strictly increasing and contiguous, so the periods partition
 * time, and every lesson falls in exactly one of them. Never two, never none.
 *
 * A change lands at the NEXT boundary, never mid-period (D3, owner's choice): the
 * running period finishes on its old rule, and the gap to the new rule's first
 * boundary becomes one short bridge period. Changing 26th→1st on 10 Sep gives
 * [26 Aug, 26 Sep) unchanged, then the bridge [26 Sep, 1 Oct), then [1 Oct, 1 Nov)
 * and onward. Nothing already on a teacher's screen moves.
 *
 * A period is named for the month its LAST DAY falls in — [26 Aug, 26 Sep) is
 * "Sep", [1 Oct, 1 Nov) is "Oct" — so the naming is the same rule whether the
 * cycle is a payroll window or a plain calendar month. A bridge can share its
 * name with its neighbour ("Sep" twice, once ever per change); the printed date
 * range (022 D23) is what tells them apart, which is what that range is for.
 */

/** Day-of-month bounds. 29–31 is refused: those days don't exist in February,
 *  so a cycle pinned to one would silently skip or double a month every year. */
export const MIN_START_DAY = 1;
export const MAX_START_DAY = 28;

/** What the app uses when `config/app` has no `payCycle` — today's live behaviour. */
export const DEFAULT_START_DAY = 26;

// How far back to build the timeline when nothing older forces us further. Six
// chart buckets is the deepest any screen reads; 36 months is slack, not a limit.
const ORIGIN_MONTHS_BACK = 36;
// Runaway guard. Only reachable if a stored `effectiveFrom` is absurdly old.
const MAX_BOUNDARIES = 4000;

/** @returns {Date|null} null for anything that isn't a real instant. */
function toDate(value) {
  if (!value) return null;
  const date = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isValidStartDay(day) {
  return Number.isInteger(day) && day >= MIN_START_DAY && day <= MAX_START_DAY;
}

/**
 * Stored versions → a sorted, validated list safe to walk. Anything malformed is
 * dropped rather than guessed at; an empty or unusable list falls back to the
 * single default version, so a broken config degrades to today's behaviour
 * instead of to no periods at all.
 *
 * The FIRST version's `effectiveFrom` is deliberately ignored: the oldest rule
 * governs all of history before the second one, so there is nothing before it to
 * date. Only later entries carry a meaningful start.
 *
 * @returns {Array<{startDay: number, effectiveFrom: Date|null}>} never empty
 */
export function normaliseVersions(raw) {
  const list = Array.isArray(raw) ? raw : [];
  const parsed = list
    .map((v) => ({ startDay: Number(v?.startDay), effectiveFrom: toDate(v?.effectiveFrom) }))
    .filter((v) => isValidStartDay(v.startDay))
    .sort((a, b) => {
      // Undated entries sort first — they can only be the original rule.
      if (!a.effectiveFrom) return -1;
      if (!b.effectiveFrom) return 1;
      return a.effectiveFrom - b.effectiveFrom;
    });

  if (parsed.length === 0) return [{ startDay: DEFAULT_START_DAY, effectiveFrom: null }];
  return parsed.map((v, i) => (i === 0 ? { startDay: v.startDay, effectiveFrom: null } : v));
}

/** The version in force at instant `t` — the last one that had started by then. */
function governing(versions, t) {
  let found = versions[0];
  for (const v of versions) {
    if (v.effectiveFrom && v.effectiveFrom > t) break;
    found = v;
  }
  return found;
}

/** The earliest local midnight on day-of-month `day` strictly after `from`. */
function nextOccurrence(from, day) {
  const candidate = new Date(from.getFullYear(), from.getMonth(), day, 0, 0, 0, 0);
  if (candidate > from) return candidate;
  return new Date(from.getFullYear(), from.getMonth() + 1, day, 0, 0, 0, 0);
}

/**
 * Every period boundary from a safe origin up to past `through`, ascending.
 *
 * The origin has to be a point where we genuinely KNOW a boundary, or the whole
 * chain is off by a few days. It is taken at or before both `through` minus 36
 * months and the *second* version's start, which puts it inside the first
 * version's era — where that version's own day-of-month is, by definition, a
 * boundary. Walking forward from there re-derives every later change exactly.
 */
function boundaries(versions, through) {
  const backstop = new Date(through.getFullYear(), through.getMonth() - ORIGIN_MONTHS_BACK, 1);
  const secondStart = versions[1]?.effectiveFrom || null;
  const origin = secondStart && secondStart < backstop ? secondStart : backstop;

  const first = versions[0];
  let cursor = new Date(origin.getFullYear(), origin.getMonth(), first.startDay, 0, 0, 0, 0);
  if (cursor > origin) cursor = new Date(origin.getFullYear(), origin.getMonth() - 1, first.startDay, 0, 0, 0, 0);

  const out = [cursor];
  // One boundary past `through` so the caller always has a closing edge.
  while (cursor <= through && out.length < MAX_BOUNDARIES) {
    cursor = nextOccurrence(cursor, governing(versions, cursor).startDay);
    out.push(cursor);
  }
  return out;
}

/**
 * The period containing `now`, shifted by `offset` whole periods.
 *
 * Periods vary in length once the rule has changed, so callers must take both
 * edges from here — `end` is the next period's `start`, never "start + a month".
 *
 * @returns {{start: Date, end: Date}}
 */
export function cyclePeriod(versions, now, offset = 0) {
  const list = normaliseVersions(versions);
  // Build far enough ahead that a positive offset still lands inside the chain.
  const horizon = new Date(now.getFullYear(), now.getMonth() + Math.max(0, offset) + 2, 1);
  const edges = boundaries(list, horizon);

  let index = 0;
  for (let i = 0; i < edges.length - 1; i += 1) {
    if (edges[i] <= now && now < edges[i + 1]) {
      index = i;
      break;
    }
  }

  const target = index + offset;
  // Before the origin there is no recorded rule to slice by; clamp rather than
  // invent periods, so a chart asking for six buckets on a new install shows the
  // oldest one repeatedly instead of dates nobody's rule ever produced.
  const safe = Math.min(Math.max(target, 0), edges.length - 2);
  return { start: edges[safe], end: edges[safe + 1] };
}

/** The period's last day — `end` is exclusive, so this is the instant before it. */
export function lastDayOf(period) {
  return new Date(period.end.getTime() - 1);
}

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * A period is named for the month its last day falls in: [26 Aug, 26 Sep) is "Sep",
 * [1 Oct, 1 Nov) is "Oct". One rule whether the cycle is a payroll window or a plain
 * calendar month — and a bridge may share a neighbour's name, which `periodRange`
 * exists to disambiguate.
 */
export function periodLabel(period) {
  return MONTH_NAMES[lastDayOf(period).getMonth()];
}

/** "26 Aug – 25 Sep" — the dates the period actually covers (022 D23). */
export function periodRange(period) {
  const last = lastDayOf(period);
  return `${period.start.getDate()} ${MONTH_NAMES[period.start.getMonth()]} – ${last.getDate()} ${MONTH_NAMES[last.getMonth()]}`;
}
